import { t } from '@lingui/core/macro';
import { isChunkLoadError } from '@luminovo/commons';
import { CenteredLayout, NonIdealState, TriangleAlertFilledIcon, useNavigate } from '@luminovo/design-system';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { ArrowLeftIcon } from 'lucide-react';
import type { JSX } from 'react';

import { chooseNewNavigation } from '@/app/newNavigation';
import { NewNavigationShell } from '@/app/NewNavigation/NewNavigationShell';
import { RouteErrorComponent } from '@/app/routerDefaults';
import { canUseNewNavigation } from '@/shared/permissions/pagePermissions';
import type { PageStaticData } from '@/shared/routing';
import { isAppStale } from '@/shared/utils/staleApp';

// The new navigation's route tree: a page in it has a URL of its own, below `/next`, and renders in its shell.
export const Route = createFileRoute('/_authenticated/next')({
    staticData: { access: { permissions: canUseNewNavigation }, chrome: 'none' } satisfies PageStaticData,
    component: RouteComponent,
    // A failing page shows its own error inside the shell; this is the shell failing, which holds the switch back.
    errorComponent: ErrorComponent,
});

function RouteComponent(): JSX.Element {
    return (
        <NewNavigationShell>
            <Outlet />
        </NewNavigationShell>
    );
}

function ErrorComponent({ error }: { error: unknown }): JSX.Element {
    const navigate = useNavigate();
    // A chunk lost to a release: the app's error page offers the reload that fixes it, and the choice stays.
    if (isChunkLoadError(error) || isAppStale()) {
        return <RouteErrorComponent error={error} />;
    }
    return (
        <CenteredLayout>
            <NonIdealState
                variant="error"
                icon={<TriangleAlertFilledIcon />}
                title={t`Unexpected error`}
                description={t`The new navigation ran into an error. Go back to the old one to keep working.`}
                action={{
                    onClick: () => {
                        chooseNewNavigation(false);
                        navigate({ to: '/' });
                    },
                    startIcon: <ArrowLeftIcon />,
                    children: t`Back to the old navigation`,
                }}
            />
        </CenteredLayout>
    );
}
